import { QuoteOfferingWithChildren } from '@monetize/types/app';
import { ReactNode } from 'react';
import { formatQuoteOfferingsSubtotal } from '../../../../../utils/quotes';
import { useQuoteContext } from '../../quoteContext';
import { QuoteOfferingGroupBox } from './QuoteOfferingGroupBox';
import {
  getGroupToggleLabel,
  QuoteOfferingGroupHeaderShell,
} from './QuoteOfferingGroupHeaderShell';

interface QuoteOfferingUngroupedContainerProps {
  /** The offerings in no group, used for the count and the subtotal */
  offerings: QuoteOfferingWithChildren[];
  /** Unique per rendered container — see QuoteOfferingGroupContainer */
  containerKey: string;
  /** Table section this container sits in, when the table has more than one */
  sectionLabel?: string;
  isOpen: boolean;
  onIsOpenChange: (isOpen: boolean) => void;
  children?: ReactNode;
}

/**
 * Container for the offerings that belong to no group. Rendered only once a
 * quote has groups: a bare run of rows below the last group box read as part
 * of that group, so everything on a grouped quote gets a box of its own.
 *
 * There is no add row here — the table's own add row already appends
 * ungrouped offerings.
 */
export const QuoteOfferingUngroupedContainer = ({
  offerings,
  containerKey,
  sectionLabel,
  isOpen,
  onIsOpenChange,
  children,
}: QuoteOfferingUngroupedContainerProps) => {
  const {
    quoteData: { quote },
  } = useQuoteContext();

  const subtotal = formatQuoteOfferingsSubtotal(offerings, quote?.currency);

  return (
    <QuoteOfferingGroupBox data-testid={`quote-offering-group-${containerKey}`}>
      <QuoteOfferingGroupHeaderShell
        title="Ungrouped"
        offeringCount={offerings.length}
        subtotal={subtotal}
        isOpen={isOpen}
        onToggle={() => onIsOpenChange(!isOpen)}
        toggleLabel={getGroupToggleLabel('Ungrouped', sectionLabel)}
        testId={`quote-offering-group-header-${containerKey}`}
      />
      {isOpen && children}
    </QuoteOfferingGroupBox>
  );
};
